import React, { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';
import PhotoCropper from './PhotoCropper';
import { useCart } from '../contexts/CartContext';

export default function PhotoUpload({ product, selectedOptions, finalPrice, onClose }) {
  const { addToCart } = useCart();
  // Calculate required photo count based on selected options
  const getRequiredPhotoCount = () => {
    if (!product.options) return 8;
    
    // Look for pages option
    const pagesOption = product.options.find(opt => 
      opt.name.toLowerCase().includes('page') || 
      opt.name.toLowerCase().includes('pages') ||
      opt.name.toLowerCase().includes('picture') ||
      opt.name.toLowerCase().includes('pictures')
    );
    
    if (pagesOption) {
      const selectedChoiceIndex = selectedOptions[product.options.indexOf(pagesOption)];
      const selectedChoice = pagesOption.choices[selectedChoiceIndex];
      if (selectedChoice.includes('16')) return 16;
    }
    
    return 8; // Default to 8 photos
  };

  const requiredPhotoCount = getRequiredPhotoCount();

  const [uploadedPhotos, setUploadedPhotos] = useState(Array(requiredPhotoCount).fill(null));
  const [croppedPhotos, setCroppedPhotos] = useState([]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [previewIndex, setPreviewIndex] = useState(null);
  
  // Store crop state for each photo to preserve user's work
  const [cropStates, setCropStates] = useState({});
  
  const fileInputRef = useRef(null);

  // Handle multiple file uploads
  const handleMultipleFileUpload = (files) => {
    const fileArray = Array.from(files);
    const maxFiles = requiredPhotoCount;
    
    if (fileArray.length > maxFiles) {
      setMessage(`⚠️ You selected ${fileArray.length} photos, but only ${maxFiles} are needed. Using the first ${maxFiles} photos.`);
      // Use only the first maxFiles
      fileArray.splice(maxFiles);
    }

    // Fill available slots with photos
    const newPhotos = [...uploadedPhotos];
    fileArray.forEach((file, index) => {
      if (index < maxFiles) {
        newPhotos[index] = {
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          uploaded: true,
          slotIndex: index
        };
      }
    });

    setUploadedPhotos(newPhotos);
    
    // Automatically open crop modal for the first photo
    if (fileArray.length > 0) {
      setCurrentPhotoIndex(0);
      setShowCropModal(true);
    }
    
    if (fileArray.length > maxFiles) {
      setTimeout(() => setMessage(''), 5000); // Clear message after 5 seconds
    }
  };

  // Handle single file upload for specific slot
  const handleFileUpload = (index, file) => {
    const newPhoto = {
      id: uuidv4(),
      file,
      preview: URL.createObjectURL(file),
      uploaded: true,
      slotIndex: index
    };
    
    setUploadedPhotos(prev => {
      const newPhotos = [...prev];
      newPhotos[index] = newPhoto;
      return newPhotos;
    });

    // Automatically open crop modal for this photo
    setCurrentPhotoIndex(index);
    setShowCropModal(true);
  };

  // Remove photo from slot
  const removePhoto = (index) => {
    const photoToRemove = uploadedPhotos[index];
    if (photoToRemove?.preview) {
      URL.revokeObjectURL(photoToRemove.preview);
    }
    
    // Remove from cropped photos as well
    setCroppedPhotos(prev => prev.filter(p => p.slotIndex !== index));
    
    setUploadedPhotos(prev => {
      const newPhotos = [...prev];
      newPhotos[index] = null;
      return newPhotos;
    });
  };

  // Open crop modal for specific photo
  const openCropModal = (index) => {
    if (uploadedPhotos[index]) {
      setCurrentPhotoIndex(index);
      setShowCropModal(true);
    }
  };

  // Find the next photo that needs cropping
  const findNextUncroppedPhoto = () => {
    for (let i = 0; i < uploadedPhotos.length; i++) {
      if (uploadedPhotos[i] && !croppedPhotos.find(p => p.slotIndex === i)) {
        return i;
      }
    }
    return -1; // All photos are cropped
  };

  // Upload all photos to Firebase Storage
  const uploadPhotos = async () => {
    if (croppedPhotos.length < requiredPhotoCount) {
      setMessage('❌ Please crop all photos before adding to cart');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const uploadPromises = croppedPhotos.map(async (photo, idx) => {
        const path = `user-photos/${photo.id}_${Date.now()}.jpg`;
        console.log('[PhotoUpload] uploading to', path, 'size', photo.croppedBlob.size);
        const imageRef = ref(storage, path);
        try {
          const snap = await uploadBytes(imageRef, photo.croppedBlob);
          const url = await getDownloadURL(snap.ref);
          return url;
        } catch (err) {
          console.error('[PhotoUpload] upload failed at index', idx, err);
          throw err;
        }
      });

      const uploadedUrls = await Promise.all(uploadPromises);

      const photosWithMeta = croppedPhotos
        .sort((a,b)=>a.slotIndex-b.slotIndex)
        .map((p, idx) => ({ url: uploadedUrls[idx], crop: p.cropMeta }));

      const item = {
        productId: product.id,
        title: product.title,
        options: selectedOptions,
        price: finalPrice,
        photos: photosWithMeta,
      };
      console.log('[PhotoUpload] adding item to cart', item);
      try {
        const id = await addToCart(item);
        console.log('[PhotoUpload] addToCart resolved with id', id);
      } catch (err) {
        console.error('[PhotoUpload] addToCart failed', err);
        setMessage('❌ Failed to save cart: ' + (err?.message || 'unknown'));
        return;
      }
      setMessage('✅ Added to cart. Redirecting to summary...');
      window.location.href = '/order-summary';

    } catch (error) {
      console.error('Error uploading photos:', error);
      setMessage('❌ Error uploading photos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Manual Photo Album Preview component (3D photoflip box)
  const ManualAlbumPreview = ({ photos }) => {
    const [currentAlbumIndex, setCurrentAlbumIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playInterval, setPlayInterval] = useState(null);
    const [isFlipping, setIsFlipping] = useState(false);

    const startAutoPlay = () => {
      if (isPlaying || photos.length === 0) return;
      setIsPlaying(true);
      const interval = setInterval(() => {
        triggerFlip(1);
      }, 2000);
      setPlayInterval(interval);
    };

    const stopAutoPlay = () => {
      if (!isPlaying) return;
      setIsPlaying(false);
      if (playInterval) {
        clearInterval(playInterval);
        setPlayInterval(null);
      }
    };

    const triggerFlip = (delta) => {
      if (isFlipping) return;
      setIsFlipping(true);
      setTimeout(() => {
        setCurrentAlbumIndex(prev => (prev + delta + photos.length) % photos.length);
        setIsFlipping(false);
      }, 450); // match CSS transition duration
    };

    const nextPhoto = () => triggerFlip(1);
    const prevPhoto = () => triggerFlip(-1);

    React.useEffect(() => () => playInterval && clearInterval(playInterval), [playInterval]);

    if (photos.length === 0) return null;

    const currentSrc = photos[currentAlbumIndex]?.preview;
    const nextSrc = photos[(currentAlbumIndex + 1) % photos.length]?.preview;

    return (
      <div className="preview-section">
        <h3>Manual Photo Album Preview</h3>
        <p className="preview-description">See how your final photobox will look!</p>

        <div className="flipbox-preview">
          <div className={`flipbox-box vertical ${isFlipping ? 'is-flipping' : ''}`}>
            <div className="flipbox-wood" />
            <div className="flipbox-viewport">
              <div className="flip-panel flip-current">
                <img src={currentSrc} alt="Current" />
              </div>
              <div className="flip-panel flip-next">
                <img src={nextSrc} alt="Next" />
              </div>
            </div>
            <div className={`flipbox-crank ${isPlaying ? 'spin' : ''}`} onClick={isPlaying ? stopAutoPlay : startAutoPlay} title={isPlaying ? 'Pause' : 'Play'}>
              <div className="crank-stick" />
              <div className="crank-knob" />
            </div>
          </div>

          <div className="album-controls below">
            <button className="control-btn prev" onClick={prevPhoto} title="Previous">
              ◀
            </button>
            <button className={`control-btn ${isPlaying ? 'pause' : 'play'}`} onClick={isPlaying ? stopAutoPlay : startAutoPlay} title={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button className="control-btn next" onClick={nextPhoto} title="Next">
              ▶
            </button>
          </div>

          <div className="album-info">
            <p>Photo {currentAlbumIndex + 1} of {photos.length}</p>
            <p className="album-instruction">Use the crank or controls to flip through photos</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="photo-upload-modal">
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-content">
        <div className="modal-header">
          <h2>Customize Your Photobox</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          {/* Multiple Photo Upload Section */}
          <div className="upload-section">
            <h3>📸 Photo Uploads ({requiredPhotoCount} photos needed)</h3>
            
            {/* Multiple File Upload */}
            <div className="multiple-upload-section">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleMultipleFileUpload(e.target.files)}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="multiple-upload-btn"
              >
                📁 Upload Multiple Photos at Once
              </button>
              <p className="upload-help">
                Select {requiredPhotoCount} photos at once, or upload them individually below
              </p>
            </div>

            {/* Individual Photo Slots */}
            <div className="photo-upload-grid">
              {Array.from({ length: requiredPhotoCount }, (_, index) => {
                const photo = uploadedPhotos[index];
                const croppedPhoto = croppedPhotos.find(p => p.slotIndex === index);
                
                return (
                  <div key={index} className="photo-slot">
                    {photo ? (
                      <div className="photo-preview">
                        <img 
                          src={croppedPhoto?.preview || photo.preview} 
                          alt={`Photo ${index + 1}`} 
                          className="slot-image" 
                        />
                        <button 
                          type="button"
                          className="slot-preview-link"
                          onClick={() => setPreviewIndex(index)}
                          title="Preview"
                        >
                          Preview
                        </button>
                        <div className="photo-actions">
                          <button 
                            onClick={() => removePhoto(index)} 
                            className="remove-slot-btn" 
                            title="Remove Photo"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="photo-upload-slot small-upload-tile">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileUpload(index, e.target.files[0]);
                            }
                          }}
                          id={`photo-upload-${index}`}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor={`photo-upload-${index}`} className="upload-label small-upload-label">
                          <div className="upload-icon small-upload-icon">
                            <span className="icon">📷</span>
                            <span className="plus">+</span>
                          </div>
                          <span className="slot-label small-slot-label">Photo {index + 1}</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Instruction Box */}
          <div className="instruction-box">
            <h4>📋 Important Instructions</h4>
            <p>
              <strong>Dashed lines in the preview image indicate the cutting positions.</strong> 
              Do not place eyes or important facial features on the dashed lines. 
              The crop area is fixed at 1080x870 pixels - you can only move the image around to position it correctly.
            </p>
          </div>

          {/* Manual Photo Album Preview */}
          {croppedPhotos.length > 0 && (
            <ManualAlbumPreview photos={croppedPhotos} />
          )}

          {/* Message Display */}
          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : message.includes('⚠️') ? 'warning' : 'error'}`}>
              {message}
            </div>
          )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <button onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button 
              onClick={uploadPhotos} 
              disabled={loading || croppedPhotos.length < requiredPhotoCount} 
              className="add-to-cart-btn"
            >
              {loading ? 'Processing...' : 'ADD TO CART'}
            </button>
          </div>
        </div>
      </div>

      {/* Photo Cropper Modal */}
      {showCropModal && (
        <PhotoCropper
          imageSrc={uploadedPhotos[currentPhotoIndex]?.preview}
          initialCropState={cropStates[currentPhotoIndex]}
          onComplete={(croppedImage, meta) => {
            // Handle the cropped image
            const croppedPhoto = {
              id: uploadedPhotos[currentPhotoIndex].id,
              originalFile: uploadedPhotos[currentPhotoIndex].file,
              croppedBlob: croppedImage,
              preview: URL.createObjectURL(croppedImage),
              cropMeta: meta,
              cropped: true,
              slotIndex: uploadedPhotos[currentPhotoIndex].slotIndex
            };
            
            setCroppedPhotos(prev => {
              const filtered = prev.filter(p => p.slotIndex !== croppedPhoto.slotIndex);
              return [...filtered, croppedPhoto];
            });
            
            setShowCropModal(false);
            
            // Check if there are more photos to crop
            const nextPhotoIndex = findNextUncroppedPhoto();
            if (nextPhotoIndex !== -1) {
              setCurrentPhotoIndex(nextPhotoIndex);
              setShowCropModal(true);
            }
          }}
          onCancel={() => setShowCropModal(false)}
        />
      )}

      {previewIndex !== null && (
        <div className="fixed-preview-modal" role="dialog" aria-modal="true" onClick={() => setPreviewIndex(null)}>
          <div className="fixed-preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="fixed-preview-close" onClick={() => setPreviewIndex(null)} aria-label="Close">×</button>
            <img
              src={(croppedPhotos.find(p => p.slotIndex === previewIndex)?.preview) || uploadedPhotos[previewIndex]?.preview}
              alt={`Preview photo ${previewIndex + 1}`}
              className="fixed-preview-image"
            />
            <div className="fixed-preview-actions">
              <button
                className="fixed-preview-edit"
                onClick={() => {
                  setShowCropModal(true);
                  setCurrentPhotoIndex(previewIndex);
                  setPreviewIndex(null);
                }}
              >
                ✂️ Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
